import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, IconButton, TextField } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * FileUploadField: Deferred file upload component for DynamicForm
 * 
 * Files are NOT uploaded immediately. Instead, they are stored with metadata
 * and uploaded AFTER the parent form is successfully submitted.
 * 
 * Props:
 * - label: Button label (default: 'Attach Documents')
 * - name: Field name in formData
 * - objectType: Type of entity (CTP, TAR, TR, etc.) - REQUIRED
 * - formSectionId: Optional UUID for form section categorization
 * - onFilesChange: Callback when files array changes
 * - value: Current files array from formData (for controlled component)
 */
export default function FileUploadField(props) {
  // Use controlled value from parent formData or fallback to local state
  const files = props.value || [];

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    const filesWithMetadata = newFiles.map(file => ({
      file,
      name: file.name,
      description: '',
      // Metadata will be completed by DynamicForm after form submission:
      // - objectId (from form submission response)
      // - objectType (from field config)
      // - creatorContactId (from user context)
      // - formSectionId (optional, from field config)
    }));
    
    const updated = [...files, ...filesWithMetadata];
    props.onFilesChange?.(updated);
  };

  const handleRemoveFile = (index) => {
    const updated = files.filter((_, i) => i !== index);
    props.onFilesChange?.(updated);
  };

  const handleDescriptionChange = (index, description) => {
    const updated = [...files];
    updated[index] = { ...updated[index], description };
    props.onFilesChange?.(updated);
  };

  return (
    <Box>
      <Button
        variant="outlined"
        component="label"
        startIcon={<AttachFileIcon />}
        sx={{ mb: 2 }}
        disabled={props.readOnly}
      >
        {props.label || 'Attach Documents'}
        <input type="file" multiple hidden onChange={handleFileChange} disabled={props.readOnly} />
      </Button>
      
      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>
            Files will be uploaded after form submission:
          </Typography>
          {files.map((fileObj, index) => {
            const fileName = fileObj.file?.name || fileObj.name;
            return (
              <Box
                key={index}
                sx={{ 
                  mb: 2, 
                  p: 2, 
                  border: 1, 
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'action.hover'
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {fileName}
                  </Typography>
                  {!props.readOnly && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Description (optional)"
                  value={fileObj.description || ''}
                  onChange={(e) => handleDescriptionChange(index, e.target.value)}
                  disabled={props.readOnly}
                  placeholder="Add a description for this file"
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
